import { useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { ArrowRightIcon, ArrowLeftIcon } from "@primer/octicons-react";
import store from "../store";
import { useSnapshot } from "valtio";
import { fetchUserDetails } from "../hooks/userSearch";

const Content = tw.div`flex flex-col justify-center p-6 text-gray-900 font-semibold`;

const HeaderContainer = tw.header`px-5 py-4 border-b border-gray-900`;

const Header = tw.h2``;

const TableContainer = tw.div`w-full max-w-5xl mx-auto shadow-lg rounded bg-gray-100 overflow-hidden`;

const Table = styled.table`
  ${tw`w-full`}
  table-layout: fixed;
  min-width: 42rem;

  @media (min-width: 640px) {
    min-width: 56rem;
  }
`;

const TablePadding = tw.div`overflow-x-auto`;

const TableThead = tw.thead`text-xs font-semibold uppercase bg-gray-200`;

const TableRow = tw.tr``;

const TableHeader = tw.th`p-4 whitespace-nowrap font-semibold text-left`;

const CompactTableHeader = styled(TableHeader)`
  padding: 0.75rem 0.5rem;

  @media (min-width: 640px) {
    padding: 1rem;
  }
`;

const DeveloperTableHeader = styled(CompactTableHeader)`
  width: 12rem;

  @media (min-width: 640px) {
    width: 16rem;
  }
`;

const ProfileTableHeader = styled(CompactTableHeader)`
  width: 8rem;

  @media (min-width: 640px) {
    width: 10rem;
  }
`;

const CountTableHeader = styled(CompactTableHeader)`
  width: 4.75rem;

  @media (min-width: 640px) {
    width: 6rem;
  }
`;

const BioTableHeader = styled(CompactTableHeader)`
  width: 11rem;

  @media (min-width: 640px) {
    width: auto;
  }
`;

const TableBody = tw.tbody`text-sm divide-y divide-gray-900`;

const TableDataCell = tw.td`p-4 align-top`;

const CompactTableDataCell = styled(TableDataCell)`
  padding: 0.75rem 0.5rem;

  @media (min-width: 640px) {
    padding: 1rem;
  }
`;

const CountTableDataCell = styled(CompactTableDataCell)`
  text-align: right;
`;

const TableDataNameContainer = tw.div`flex items-center min-w-0`;

const TableDataImage = tw.img`w-10 h-10 flex-shrink-0 mr-2 sm:mr-3 rounded-full`;

const TableDataName = tw.div`font-medium truncate`;

const TableDataSecondary = tw.div`text-xs font-medium text-gray-600 mt-1`;

const TableDataRealName = tw.div`text-xs font-medium text-gray-600 truncate`;

const TableDataDetailsBio = styled.div`
  ${tw`text-xs font-medium text-gray-600 leading-relaxed whitespace-normal`}
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const TableDataMuted = tw.span`text-xs font-medium text-gray-500`;

const TableDataMeta = tw.div`font-medium text-gray-100 text-left flex flex-row`;

const TableDataMetaLink = tw.a`inline-flex items-center justify-center rounded shadow cursor-pointer bg-primary-600 transition duration-300 hocus:bg-primary-800 py-2 px-3 whitespace-nowrap w-full`;

const TableDataStatus = tw.div`text-xs font-medium text-gray-500`;

const DesktopLabel = tw.span`hidden sm:inline`;

const MobileLabel = tw.span`sm:hidden`;

const NavContainer = tw.nav`px-5 py-4 border-t border-gray-900 justify-between flex`;

const NavNextPageIcon = tw(ArrowRightIcon)``;

const NavNextPage = tw.button`cursor-pointer transition duration-300 hover:text-primary-500 disabled:cursor-not-allowed disabled:hover:text-secondary-100 disabled:text-secondary-100`;

const NavPreviousPageIcon = tw(ArrowLeftIcon)``;

const NavPreviousPage = tw(NavNextPage)``;

const NavPageInfo = tw.span``;

const DETAILS_FETCH_CONCURRENCY = 4;

export default function UserTable() {
  const snap = useSnapshot(store);
  const users = snap.users || [];
  const visibleUsernames = users.map((user) => user.login).join(",");

  useEffect(() => {
    let isActive = true;

    const loadVisibleUserDetails = async () => {
      const usernames = visibleUsernames
        .split(",")
        .filter(Boolean)
        .filter(
          (username) =>
            !snap.userDetails[username] &&
            !snap.loadingUserDetails[username] &&
            !snap.userDetailsError[username]
        );

      for (
        let index = 0;
        isActive && index < usernames.length;
        index += DETAILS_FETCH_CONCURRENCY
      ) {
        await Promise.all(
          usernames
            .slice(index, index + DETAILS_FETCH_CONCURRENCY)
            .map((username) => fetchUserDetails(username))
        );
      }
    };

    loadVisibleUserDetails();

    return () => {
      isActive = false;
    };
  }, [snap.loadingUserDetails, snap.userDetails, snap.userDetailsError, visibleUsernames]);

  const handleNextPage = () => {
    if (store.page < store.pageCount) {
      store.page++;
    }
  };

  const handlePreviousPage = () => {
    if (store.page > 1) {
      store.page--;
    }
  };

  return (
    <>
      <Content>
        <TableContainer>
          <HeaderContainer>
            <Header>
              {!snap.error
                ? snap.totalUsersCount === 0
                  ? `Find GitHub users by location...`
                  : `${snap.totalUsersCount} GitHub users found in ${snap.userLocation}`
                : `API rate limit exceeded. Please try again in a few moments...`}
            </Header>
          </HeaderContainer>
          <TablePadding>
            <Table>
              <TableThead>
                <TableRow>
                  <DeveloperTableHeader>Developer</DeveloperTableHeader>
                  <CountTableHeader>
                    <DesktopLabel>Followers</DesktopLabel>
                    <MobileLabel>Follows</MobileLabel>
                  </CountTableHeader>
                  <CountTableHeader>Repos</CountTableHeader>
                  <BioTableHeader>Bio</BioTableHeader>
                  <ProfileTableHeader>Profile</ProfileTableHeader>
                </TableRow>
              </TableThead>
              <TableBody>
                {users.map((user) => {
                  const details = snap.userDetails[user.login];
                  const isLoadingDetails = snap.loadingUserDetails[user.login];
                  const detailsError = snap.userDetailsError[user.login];

                  return (
                    <TableRow key={user.login}>
                      <CompactTableDataCell>
                        <TableDataNameContainer>
                          <TableDataImage src={`${user.avatar_url}`} />
                          <div>
                            <TableDataName>{user.login}</TableDataName>
                            {details?.name ? (
                              <TableDataRealName>
                                {details.name}
                              </TableDataRealName>
                            ) : null}
                            <TableDataSecondary>{user.type}</TableDataSecondary>
                          </div>
                        </TableDataNameContainer>
                      </CompactTableDataCell>

                      <CountTableDataCell>
                        {details ? (
                          details.followers.toLocaleString()
                        ) : detailsError ? (
                          <TableDataStatus>{detailsError}</TableDataStatus>
                        ) : (
                          <TableDataStatus>
                            {isLoadingDetails ? "Loading" : "Queued"}
                          </TableDataStatus>
                        )}
                      </CountTableDataCell>

                      <CountTableDataCell>
                        {details ? (
                          details.public_repos.toLocaleString()
                        ) : (
                          <TableDataMuted>...</TableDataMuted>
                        )}
                      </CountTableDataCell>

                      <CompactTableDataCell>
                        {details?.bio ? (
                          <TableDataDetailsBio>{details.bio}</TableDataDetailsBio>
                        ) : details ? (
                          <TableDataMuted>No bio</TableDataMuted>
                        ) : (
                          <TableDataMuted>...</TableDataMuted>
                        )}
                      </CompactTableDataCell>

                      <CompactTableDataCell>
                        <TableDataMeta>
                          <TableDataMetaLink
                            href={user.html_url}
                            target="blank"
                            rel="noopener noreferrer"
                          >
                            Visit profile
                          </TableDataMetaLink>
                        </TableDataMeta>
                      </CompactTableDataCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TablePadding>
          {store.pageCount > 2 ? (
            <NavContainer>
              <NavPreviousPage
                disabled={store.page > 1 ? false : true}
                onClick={handlePreviousPage}
              >
                <NavPreviousPageIcon size={24} />
              </NavPreviousPage>
              <NavPageInfo>
                {`Page ${store.page} of ${store.pageCount}`}
              </NavPageInfo>
              <NavNextPage
                disabled={store.page < store.pageCount ? false : true}
                onClick={handleNextPage}
              >
                <NavNextPageIcon size={24} />
              </NavNextPage>
            </NavContainer>
          ) : null}
        </TableContainer>
      </Content>
    </>
  );
}
